import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, Ref, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

/** Tailwind's compiled stylesheet orders utilities by its own internal
 * plugin order, not by where a class appears in `className` — so appending
 * an overriding class (e.g. `rounded-full`, `px-2`, `text-xs`) after these
 * components' hard-coded base classes (`rounded-md`, `px-4`, `text-sm`)
 * does NOT reliably win the cascade; whichever rule happens to land later
 * in the generated CSS wins, silently, regardless of source order. Found
 * via a real, minified-CSS check (`rounded-full`/`px-2.5` on the
 * Zu-/Absage-Buttons in `TrainingPage.tsx` computed to `rounded-md`/`px-4`
 * despite the override). `mergeClasses` drops any base-class token that
 * shares a utility group (padding, border-radius, font-size, height,
 * width) with an incoming override token, so the override always applies —
 * a tiny, dependency-free stand-in for what `tailwind-merge` would do,
 * scoped to the groups these components actually hard-code. `px-*`/`py-*`
 * are separate groups (not a single "padding" bucket): they set different
 * CSS properties, and conflating them once silently dropped a `py-*` base
 * class whenever only `px-*` was overridden (e.g. `rounded-full px-2.5` on
 * the Zu-/Absage-Buttons — the design spec explicitly said "Höhe
 * unverändert lassen", i.e. `py-2` must survive). */
const CONFLICT_GROUPS: RegExp[] = [
  /^px-/,
  /^py-/,
  /^rounded(-|$)/,
  /^text-(xs|sm|base|lg|xl|\d)/,
  /^h-/,
  /^w-/,
];

function sameUtilityGroup(a: string, b: string): boolean {
  return CONFLICT_GROUPS.some((group) => group.test(a) && group.test(b));
}

function mergeClasses(base: string, overrides: string): string {
  if (!overrides.trim()) return base;
  const overrideTokens = overrides.trim().split(/\s+/);
  const keptBase = base.split(/\s+/).filter((b) => !overrideTokens.some((o) => sameUtilityGroup(b, o)));
  return [...keptBase, ...overrideTokens].join(' ');
}

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' | 'danger' }) {
  const styles = {
    primary: 'bg-brand-accent text-white hover:bg-blue-700 disabled:opacity-50',
    ghost: 'bg-transparent text-slate-700 hover:bg-slate-100',
    danger: 'bg-red-600 text-white hover:bg-red-700',
  }[variant];
  return (
    <button
      className={mergeClasses(`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition ${styles}`, className)}
      {...props}
    />
  );
}

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={mergeClasses(
        'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent',
        className,
      )}
      {...props}
    />
  );
}

export function Textarea({
  className = '',
  ref,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { ref?: Ref<HTMLTextAreaElement> }) {
  return (
    <textarea
      ref={ref}
      className={mergeClasses(
        'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent',
        className,
      )}
      {...props}
    />
  );
}

export function Select({ className = '', ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={mergeClasses(
        'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent',
        className,
      )}
      {...props}
    />
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  // `min-w-0` (grid/flex items default to `min-width: auto`, i.e. their
  // content's own minimum size) stops a native control with a wide
  // intrinsic layout — `<input type="date">`/`type="time"` are the worst
  // offenders, especially at the 16px mobile font-size index.css forces to
  // avoid iOS' auto-zoom-on-focus (see that rule's comment) — from
  // blowing out a narrow grid column instead of actually shrinking to fit
  // it (§ Nutzerfeedback "Felder ragen aus dem eigentlichen Layout raus").
  return (
    <label className="block min-w-0 space-y-1">
      <span className="text-sm font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}

export function Card({ title, children }: { title?: string; children: ReactNode }) {
  // Mobile (§ Nutzeranfrage „mehr die Breite … ausnutzen … moderner und
  // professioneller"): randlos statt gerahmtem Kasten — kein Rahmen/Rundung/
  // Schatten/eigene Hintergrundfarbe mehr, nur eine dünne Trennlinie unten
  // als Abschnittsgrenze (iOS-Einstellungen-Stil), kein horizontaler
  // Innenabstand mehr (den stellt bereits `AppShell.tsx`s `<main>` einmalig).
  // Vorher stapelten sich hier zwei Innenabstände (`main p-4` + `Card p-5`)
  // zu fast einem Viertel verschenkter Bildschirmbreite auf dem Handy. Ab
  // `md:` weiterhin exakt der bisherige gerahmte, weiße Kasten.
  return (
    <section className="border-b border-slate-200 bg-transparent px-0 py-4 md:rounded-xl md:border md:bg-white md:px-5 md:py-5 md:shadow-sm">
      {title && <h2 className="mb-4 text-base font-semibold text-slate-800">{title}</h2>}
      {children}
    </section>
  );
}

export function Alert({ kind = 'error', children }: { kind?: 'error' | 'success'; children: ReactNode }) {
  const styles = kind === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700';
  return <div className={`rounded-md px-3 py-2 text-sm ${styles}`}>{children}</div>;
}
